import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { authService } from "../../../utils/authService";
import CourseOverview from "./CourseOverview";
import Subjects from "./Subjects";
import CourseDropdown from "./CoordinatorDashboard/CourseDropdown";
import LiveSession from "./CoordinatorDashboard/LiveSession";
import LearningProgress from "./CoordinatorDashboard/ProjectLearningProgress";
import CandidatesPerformance from "./CoordinatorDashboard/CandidatesPerformance";
import Category from "./CoordinatorDashboard/ProjectCategory";
import UpcomingEvents from "./CoordinatorDashboard/UpcomingEvents";
import "./Dashboard.css";

interface Project {
  project_id: number;
  batch_project_id: number;
  batch_project_name: string;
}

interface FiltersData {
  courses: string[];
  batches: {
    [course: string]: string[];
  };
  subjects: {
    [course: string]: {
      [batch: string]: string[];
    };
  };
  projects?: {
    [course: string]: {
      [batch: string]: Project[];
    };
  };
}

interface Student {
  name?: string;
  student_name?: string;
  student_id: string;
}

interface CategoryStats {
  sun: number;
  moon: number;
  star: number;
  rest: number;
}

interface CategoryItem {
  category_name: "SUN" | "MOON" | "STAR" | "REST" | string;
  count: number;
}

interface TopStudentApiItem {
  student_id: string;
  category_name: string;
  score: number;
  total_score: number;
  student_name: string;
}

interface CategoryApiResponse {
  students_catagory: CategoryItem[];
  students_test_category_data_top_5?: TopStudentApiItem[];
  REST?: Student[];
  STAR?: Student[];
  MOON?: Student[];
  SUN?: Student[];
}

interface CategoryData {
  count: number;
  percentage: number;
  students: Student[];
}

interface LearningProgressData {
  current_progress: number;
  phases: string[];
  ahead: CategoryData;
  behind: CategoryData;
  on_track: CategoryData;
  total_students: number;
}

interface Event {
  title: string;
  subject: string;
  date: string;
  time: string;
  datetime: string;
}

interface UpcomingEventsData {
  events: Event[];
}

interface Subject {
  title: string;
  subject: string;
  subject_id: string;
  image: string;
  duration: string;
  progress: {
    student_progress: number;
    progress: number;
  };
  status: string;
}

interface SubjectsData {
  subjects: Subject[];
}

interface PerformanceData {
  practice_mcq: {
    phase_1: number;
    phase_2: number;
    phase_3: number;
    phase_4: number;
  };
  practice_coding: {
    phase_1: number;
    phase_2: number;
    phase_3: number;
    phase_4: number;
  };
  weekly_test: {
    phase_1: number;
    phase_2: number;
    phase_3: number;
    phase_4: number;
  };
  /** Returned when project is selected; distinct from practice_coding */
  project_coding?: {
    phase_1: number;
    phase_2: number;
    phase_3: number;
    phase_4: number;
  };
  final_test: {
    phase_1: number;
    phase_2: number;
    phase_3: number;
    phase_4: number;
  };
  total_students_count: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Authentication check - redirect to login if not authenticated as Coordinator
  useEffect(() => {
    if (!sessionStorage.getItem("Coordinator")) {
      navigate('/');
      return;
    }
  }, [navigate]);

  const [filtersData, setFiltersData] = useState<FiltersData | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [selectedBatch, setSelectedBatch] = useState<string>("");
  const [selectedSubject, setSelectedSubject] = useState<string>("");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [learningProgressData, setLearningProgressData] =
    useState<LearningProgressData | null>(null);
  const [upcomingEventsData, setUpcomingEventsData] =
    useState<UpcomingEventsData | null>(null);
  const [subjectsData, setSubjectsData] = useState<SubjectsData | null>(null);
  const [performanceData, setPerformanceData] =
    useState<PerformanceData | null>(null);
  const [performanceFilter, setPerformanceFilter] = useState<string>("all");
  const [initialDashboardLoading, setInitialDashboardLoading] = useState(true);
  const [showCourseOverview, setShowCourseOverview] = useState(false);
  const [cachedTime, setCachedTime] = useState<string>("");
  const [cacheLoading, setCacheLoading] = useState(false);
  const [attendanceReport, setAttendanceReport] = useState<any>(null);
  const [selectedSession, setSelectedSession] = useState<string>("latest");
  const [userId, setUserId] = useState<string>("");
  const [categoryStats, setCategoryStats] = useState<CategoryStats | null>(null);
  const [topStudents, setTopStudents] = useState<Student[]>([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>("");
  const [categoryData, setCategoryData] = useState<CategoryApiResponse | null>(null);

  // Get userId from sessionStorage on component mount
  useEffect(() => {
    const id = sessionStorage.getItem("Id");
    if (id) {
      setUserId(id);
    }
  }, []);

  // Save filters to session storage whenever they change
  useEffect(() => {
    if (selectedCourse || selectedBatch || selectedSubject || selectedProject) {
      const filters = {
        course: selectedCourse,
        batch: selectedBatch,
        subject: selectedSubject || null,
        project: selectedProject || null,
      };
      sessionStorage.setItem("dashboardFilters", JSON.stringify(filters));
    }
  }, [selectedCourse, selectedBatch, selectedSubject, selectedProject]);

  // Restore filters from session storage when component mounts
  useEffect(() => {
    const savedFilters = sessionStorage.getItem("dashboardFilters");
    if (savedFilters && filtersData) {
      try {
        const filters = JSON.parse(savedFilters);

        // Restore course first
        if (filters.course && filtersData.courses.includes(filters.course)) {
          setSelectedCourse(filters.course);

          // Restore batch if it exists for the selected course
          if (
            filters.batch &&
            filtersData.batches[filters.course]?.includes(filters.batch)
          ) {
            setSelectedBatch(filters.batch);

            // Restore subject or project if it exists for the selected batch
            if (
              filters.subject &&
              filtersData.subjects[filters.course]?.[filters.batch]?.includes(
                filters.subject
              )
            ) {
              setSelectedSubject(filters.subject);
              setSelectedProject(null);
            } else if (filters.project && filtersData.projects?.[filters.course]?.[filters.batch]) {
              const list = filtersData.projects[filters.course][filters.batch];
              const project = typeof filters.project === "object" && filters.project?.project_id != null
                ? list.find((p) => p.project_id === filters.project.project_id && p.batch_project_id === filters.project.batch_project_id)
                : list.find((p) => p.batch_project_name === filters.project);
              if (project) {
                setSelectedProject(project);
                setSelectedSubject("");
              }
            }
          }
        }
      } catch (error) {
        console.error("Error parsing saved dashboard filters:", error);
      }
    }
  }, [filtersData]); // Run when filtersData is loaded

  const getAttendanceData = () => {
    if (!attendanceReport) return { full: 0, acceptable: 0, unacceptable: 0, total: 0 };
    
    if (selectedSession === "all") {
      return attendanceReport.All || { full: 0, acceptable: 0, unacceptable: 0, total: 0 };
    } else if (selectedSession === "latest") {
      return attendanceReport.Letest_session || { full: 0, acceptable: 0, unacceptable: 0, total: 0 };
    } else {
      const sessionId = attendanceReport.Session_title_dict?.[selectedSession];
      if (sessionId && attendanceReport.Session_wise?.[sessionId]) {
        return attendanceReport.Session_wise[sessionId];
      }
      return { full: 0, acceptable: 0, unacceptable: 0, total: 0 };
    }
  };

  const currentAttendanceData = getAttendanceData();

  // Attendance chart values (from attendanceReport API when available)
  const attendanceValues = {
    full: currentAttendanceData.full || 0,
    acceptable: currentAttendanceData.acceptable || 0,
    unacceptable: currentAttendanceData.unacceptable || 0,
  };

  // Category chart values
  const categoryValues = {
    sun: categoryStats?.sun || 0,
    moon: categoryStats?.moon || 0,
    star: categoryStats?.star || 0,
    rest: categoryStats?.rest || 0,
  };

  // Calculate total and percentages for attendance chart
  const totalAttendance = currentAttendanceData.total ?? (
    attendanceValues.full + attendanceValues.acceptable + attendanceValues.unacceptable
  );
  const fullPercentage = (attendanceValues.full / totalAttendance) * 100;
  const acceptablePercentage =
    (attendanceValues.acceptable / totalAttendance) * 100;
  const unacceptablePercentage =
    (attendanceValues.unacceptable / totalAttendance) * 100;

  // Calculate total and percentages for category chart
  const totalCategory =
    categoryValues.sun +
    categoryValues.moon +
    categoryValues.star +
    categoryValues.rest;
  const sunPercentage = (categoryValues.sun / totalCategory) * 100;
  const moonPercentage = (categoryValues.moon / totalCategory) * 100;
  const starPercentage = (categoryValues.star / totalCategory) * 100;
  const restPercentage = (categoryValues.rest / totalCategory) * 100;

  // Calculate angles for attendance donut chart
  const fullAngle = (fullPercentage / 100) * 360;
  const acceptableAngle = (acceptablePercentage / 100) * 360;
  const unacceptableAngle = (unacceptablePercentage / 100) * 360;

  // Calculate angles for category donut chart
  const sunAngle = (sunPercentage / 100) * 360;
  const moonAngle = (moonPercentage / 100) * 360;
  const starAngle = (starPercentage / 100) * 360;
  const restAngle = (restPercentage / 100) * 360;

  // Generate dynamic conic gradients
  const attendanceGradient = `conic-gradient(
    #4caf50 0deg ${fullAngle}deg,
    #42a5f5 ${fullAngle}deg ${fullAngle + acceptableAngle}deg,
    #ff9800 ${fullAngle + acceptableAngle}deg 360deg
  )`;

  const categoryGradient = `conic-gradient(
    var(--expert-color) 0deg ${sunAngle}deg, /* Expert - Sun */
    var(--intermediate-color) ${sunAngle}deg ${sunAngle + moonAngle}deg, /* Intermediate - Moon */
    var(--advanced-color) ${sunAngle + moonAngle}deg ${
      sunAngle + moonAngle + starAngle
    }deg, /* Advanced - Star */
    var(--beginner-color) ${sunAngle + moonAngle + starAngle}deg 360deg /* Beginner - Rest */
  )`;

  useEffect(() => {
    if (userId) {
      fetchFilters();
    }
  }, [userId]);

  // Handle sidebar navigation state
  useEffect(() => {
    if (location.state?.showCourseOverview) {
      setShowCourseOverview(true);
    }
    if (location.pathname === "/coordinator/dashboard" && !location.state) {
      const savedState = sessionStorage.getItem("dashboardCourseOverview");
      if (savedState === "true") {
        setShowCourseOverview(true);
      }
    }
  }, [location.state, location.pathname]);

  useEffect(() => {
    if (selectedCourse && selectedBatch && (selectedSubject || selectedProject) && userId) {
      const fetchAllData = async () => {
        let firstDataReceived = false;
        
        const hideLoader = () => {
          if (!firstDataReceived) {
            firstDataReceived = true;
            setInitialDashboardLoading(false);
          }
        };
        
        try {
          // Fetch all data in parallel
          const promises = [
            fetchLearningProgress().then((data) => { hideLoader(); return data; }).catch(() => {}),
            fetchUpcomingEvents().then((data) => { hideLoader(); return data; }).catch(() => {}),
            fetchSubjects().then((data) => { hideLoader(); return data; }).catch(() => {}),
            fetchPerformanceData().then((data) => { hideLoader(); return data; }).catch(() => {}),
            fetchAttendanceReport().then((data) => { hideLoader(); return data; }).catch(() => {}),
            fetchCategoryData().then((data) => { hideLoader(); return data; }).catch(() => {}),
          ];
          
          // Wait for all promises to complete (or fail)
          await Promise.allSettled(promises);
          
          // After all APIs complete, fetch cached time
          await fetchCachedTime();
        } catch (error) {
          console.error("Error fetching data:", error);
          hideLoader();
        }
      };
      
      fetchAllData();
    } else if (userId && selectedCourse && selectedBatch) {
      // Prevent indefinite overlay when no subject/project is selected.
      setInitialDashboardLoading(false);
    }
  }, [selectedCourse, selectedBatch, selectedSubject, selectedProject, performanceFilter, userId]);

  const fetchFilters = async () => {
    if (!userId) {
      console.error("User ID not found in session storage");
      return;
    }
    try {
      const response = await authService.get(`coordinator/filters/${userId}/`);
      setFiltersData(response.data);

      // Check if there are saved filters in session storage
      const savedFilters = sessionStorage.getItem("dashboardFilters");
      if (savedFilters) {
        try {
          const filters = JSON.parse(savedFilters);
          // Don't set defaults here - let the useEffect handle restoration
          return;
        } catch (error) {
          console.error("Error parsing saved filters:", error);
        }
      }

      // Only set default selections if no saved filters exist
      if (response.data.courses.length > 0) {
        setSelectedCourse(response.data.courses[0]);
        if (response.data.batches[response.data.courses[0]]?.length > 0) {
          setSelectedBatch(response.data.batches[response.data.courses[0]][0]);
          if (
            response.data.subjects[response.data.courses[0]]?.[
              response.data.batches[response.data.courses[0]][0]
            ]?.length > 0
          ) {
            setSelectedSubject(
              response.data.subjects[response.data.courses[0]][
                response.data.batches[response.data.courses[0]][0]
              ][0]
            );
          }
        }
      }
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const fetchLearningProgress = async () => {
    if (!userId) return;
    try {
      const filterValue = selectedSubject || (selectedProject?.batch_project_name ?? "");
      if (selectedProject) {
        const response = await authService.get(
          `coordinator/learning_progress/project/${selectedCourse}/${selectedBatch}/${filterValue}/${userId}/`
        );
        setLearningProgressData(response.data);
        return response.data;
      }
      else {
        const response = await authService.get(
          `coordinator/learning_progress/${selectedCourse}/${selectedBatch}/${filterValue}/${userId}/`
        );
        setLearningProgressData(response.data);
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching learning progress:", error);
      throw error;
    }
  };

  const fetchUpcomingEvents = async () => {
    if (!userId) return;
    try {
      const response = await authService.get(
        `coordinator/upcoming-events/${selectedCourse}/${selectedBatch}/`
      );
      setUpcomingEventsData(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching upcoming events:", error);
      throw error;
    }
  };

  const fetchSubjects = async () => {
    if (!userId) return;
    try {
      const response = await authService.get(
        `coordinator/subjects/${selectedCourse}/${selectedBatch}/`
      );
      setSubjectsData(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching subjects:", error);
      throw error;
    }
  };

  const fetchPerformanceData = async () => {
    if (!userId) return;
    try {
      const payload: Record<string, string | number> = {
        course_name: selectedCourse,
        batch_name: selectedBatch,
        type_of_progress: performanceFilter,
        user_id: userId,
      };
      if (selectedSubject) {
        payload.subject_name = selectedSubject;
        payload.project_id = "";
        payload.project_name = "";
      } else if (selectedProject) {
        payload.subject_name = "";
        payload.project_id = selectedProject.project_id;
        payload.project_name = selectedProject.batch_project_name;
      }
      const response = await authService.post(
        `coordinator/student_performance/`,
        payload
      );
      setPerformanceData(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching performance data:", error);
      setPerformanceData(null);
      throw error;
    }
  };

  const updateTopStudentsByCategory = (data: CategoryApiResponse, category: string) => {
    // Show students for selected category
    const categoryKey = category.toUpperCase() as keyof CategoryApiResponse;
    const categoryStudents = data[categoryKey] as Student[] | undefined;
    setTopStudents(categoryStudents ? categoryStudents.slice(0, 5) : []);
  };

  const fetchCategoryData = async () => {
    if (!userId) return;
    try {
      const filterValue = selectedSubject || (selectedProject?.batch_project_name ?? "");
      const response = await authService.get(
        `coordinator/get_students_catagory_by_course_batch_subject/${encodeURIComponent(
          selectedCourse
        )}/${encodeURIComponent(selectedBatch)}/${encodeURIComponent(
          filterValue
        )}/${userId}/`
      );
      const data: CategoryApiResponse = response.data;

      setCategoryData(data);

      // Map students_catagory array to CategoryStats
      const stats: CategoryStats = {
        sun: 0,
        moon: 0,
        star: 0,
        rest: 0,
      };

      if (Array.isArray(data.students_catagory)) {
        data.students_catagory.forEach((item) => {
          const name = item.category_name?.toUpperCase();
          if (name === "SUN") stats.sun = item.count || 0;
          else if (name === "MOON") stats.moon = item.count || 0;
          else if (name === "STAR") stats.star = item.count || 0;
          else if (name === "REST") stats.rest = item.count || 0;
        });
      }

      setCategoryStats(stats);

      // Set default category to SUN if not already selected
      if (!selectedCategoryFilter) {
        setSelectedCategoryFilter("SUN");
        updateTopStudentsByCategory(data, "SUN");
      } else {
        // Set top students based on selected category filter
        updateTopStudentsByCategory(data, selectedCategoryFilter);
      }
      return data;
    } catch (error) {
      console.error("Error fetching category data:", error);
      setCategoryStats(null);
      setTopStudents([]);
      setCategoryData(null);
      throw error;
    }
  };

  const defaultAttendanceReport = {
    All: { full: 0, acceptable: 0, unacceptable: 0, total: 0 },
    Letest_session: { full: 0, acceptable: 0, unacceptable: 0, total: 0 },
    Session_wise: {},
    Session_title_dict: {},
  };

  const fetchAttendanceReport = async () => {
    if (!userId) return;
    try {
      if (selectedProject) {
        setAttendanceReport(defaultAttendanceReport);
        return defaultAttendanceReport;
      }
      const payload: Record<string, string | number> = {
        course_name: selectedCourse,
        batch_name: selectedBatch,
        user_id: userId,
      };
      if (selectedSubject) {
        payload.subject_name = selectedSubject;
        payload.project_id = "";
        payload.project_name = "";
      } else if (selectedProject) {
        payload.subject_name = "";
        payload.project_id = "";
        payload.project_name = "";
      }
      const response = await authService.post(`/coordinator/attendance_report/`, payload);
      setAttendanceReport(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching attendance report:', error);
      throw error;
    }
  };

  const fetchCachedTime = async () => {
    if (!userId) return;
    try {
      setCacheLoading(true);
      const payload: Record<string, string | number> = {
        course_name: selectedCourse,
        batch_name: selectedBatch,
        type_of_progress: performanceFilter || "all",
        user_id: userId,
      };
      if (selectedSubject) {
        payload.subject_name = selectedSubject;
        payload.project_id = "";
        payload.project_name = "";
      } else if (selectedProject) {
        payload.subject_name = "";
        payload.project_id = selectedProject.project_id;
        payload.project_name = selectedProject.batch_project_name;
      } else {
        payload.subject_name = "";
        payload.project_id = "";
        payload.project_name = "";
      }
      const response = await authService.post(`coordinator/get_cached_time/`, payload);
      const cachedTimeStr = response.data.last_cached_time || "No cache data";
      // Remove IST and format the time
      const formattedTime = cachedTimeStr !== "No cache data" 
        ? cachedTimeStr.replace(" IST", "")
        : cachedTimeStr;
      setCachedTime(formattedTime);
    } catch (error) {
      console.error("Error fetching cached time:", error);
      setCachedTime("Error loading cache data");
    } finally {
      setCacheLoading(false);
    }
  };

  const clearCache = async () => {
    if (!userId) return;
    if (!selectedCourse || !selectedBatch || (!selectedSubject && !selectedProject)) {
      setCacheLoading(false);
      return;
    }
    try {
      setCacheLoading(true);
      const payload: Record<string, string | number> = {
        course_name: selectedCourse,
        batch_name: selectedBatch,
        type_of_progress: performanceFilter || "all",
        user_id: userId,
      };
      if (selectedSubject) {
        payload.subject_name = selectedSubject;
        payload.project_id = "";
        payload.project_name = "";
      } else if (selectedProject) {
        payload.subject_name = "";
        payload.project_id = selectedProject.project_id;
        payload.project_name = selectedProject.batch_project_name;
      } else {
        payload.subject_name = "";
        payload.project_id = "";
        payload.project_name = "";
      }
      await authService.post(`coordinator/clear_cache/`, payload);
      // Refresh the complete page after clearing cache
      window.location.reload();
    } catch (error) {
      console.error("Error clearing cache:", error);
    } finally {
      setCacheLoading(false);
    }
  };

  const handleCourseChange = (course: string) => {
    setSelectedCourse(course);
    setSelectedBatch("");
    setSelectedSubject("");
    setSelectedProject(null);
    setSelectedCategoryFilter("");

    // Set default batch for new course
    if (
      filtersData &&
      filtersData.batches[course] &&
      filtersData.batches[course].length > 0
    ) {
      setSelectedBatch(filtersData.batches[course][0]);
      if (
        filtersData.subjects[course] &&
        filtersData.subjects[course][filtersData.batches[course][0]] &&
        filtersData.subjects[course][filtersData.batches[course][0]].length > 0
      ) {
        setSelectedSubject(
          filtersData.subjects[course][filtersData.batches[course][0]][0]
        );
      }
      if (
        filtersData.projects?.[course] &&
        filtersData.projects[course][filtersData.batches[course][0]] &&
        filtersData.projects[course][filtersData.batches[course][0]].length > 0
      ) {
        setSelectedProject(
          filtersData.projects[course][filtersData.batches[course][0]][0]
        );
      }
    }
  };

  const handleBatchChange = (batch: string) => {
    setSelectedBatch(batch);
    setSelectedSubject("");
    setSelectedProject(null);
    setSelectedCategoryFilter("");

    // Set default subject for new batch
    if (
      filtersData &&
      filtersData.subjects[selectedCourse] &&
      filtersData.subjects[selectedCourse][batch] &&
      filtersData.subjects[selectedCourse][batch].length > 0
    ) {
      setSelectedSubject(filtersData.subjects[selectedCourse][batch][0]);
    }
    // Set default project for new batch
    if (
      filtersData &&
      filtersData.projects?.[selectedCourse] &&
      filtersData.projects[selectedCourse][batch] &&
      filtersData.projects[selectedCourse][batch].length > 0
    ) {
      setSelectedProject(filtersData.projects[selectedCourse][batch][0]);
    }
  };

  const handleProjectChange = (project: Project | null) => {
    setSelectedProject(project);
    // Clear subject when project is selected
    if (project) {
      setSelectedSubject("");
      setSelectedCategoryFilter("");
    }
  };

  const handleSubjectChange = (subject: string) => {
    setSelectedSubject(subject);
    // Clear project when subject is selected
    if (subject) {
      setSelectedProject(null);
      setSelectedCategoryFilter("");
    }
  };

  const getProgressSortDirection = (category: string): "asc" | "desc" | undefined => {
    if (category === "behind") return "asc";
    if (category === "ahead") return "desc";
    return undefined;
  };

  const handleShowMore = (category: string) => {
      navigate("/coordinator/learning-progress", {
      state: {
        category,
        direction: getProgressSortDirection(category),
        course: selectedCourse,
        batch: selectedBatch,
        subject: selectedSubject || null,
        project: selectedProject || null,
      },
    });
  };

  const handleShowMoreTopStudents = () => {
    navigate("/coordinator/reports", {
      state: {
        course: selectedCourse,
        batch: selectedBatch,
        subject: selectedSubject || null,
        project: selectedProject || null,
        final_test_category: selectedCategoryFilter || "",
      },
    });
  };

  const handleCourseOverviewClick = () => {
    setShowCourseOverview(true);
    // Save to session storage for persistence
    sessionStorage.setItem("dashboardCourseOverview", "true");
  };


  return (
    <>
      {initialDashboardLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Spinner animation="border" role="status" />
        </div>
      )}
      {showCourseOverview ? (
         <CourseOverview onBack={() => {
           setShowCourseOverview(false);
           sessionStorage.removeItem("dashboardCourseOverview");
         }} />
       ) : (
        <div className="p-0 pt-2 overflow-x-hidden">
          <div className="container-fluid px-2">
            {/* Row 1: Filters | Attendance (~29% each) | Learning Progress (~42%) */}
            <div className="row g-3 align-items-stretch">
              <div className="col-12 col-xxl-7 d-flex flex-column overflow-hidden">
                <div className="d-flex flex-column flex-md-row gap-3 flex-grow-1 w-100">
                  <div className="flex-fill d-flex overflow-hidden">
                    <div className="card shadow-sm border flex-grow-1 w-100">
                      <div className="card-body d-flex flex-column h-100">
                        <CourseDropdown
                          filtersData={filtersData}
                          selectedCourse={selectedCourse}
                          selectedBatch={selectedBatch}
                          selectedSubject={selectedSubject}
                          selectedProject={selectedProject}
                          onCourseChange={handleCourseChange}
                          onBatchChange={handleBatchChange}
                          onSubjectChange={handleSubjectChange}
                          onProjectChange={handleProjectChange}
                        />
                        <div className="d-flex flex-column flex-sm-row gap-2 mt-3">
                          <div className="student-count-box h-auto py-2 flex-fill">
                            <span>
                              {learningProgressData
                                ? learningProgressData.ahead.count +
                                  learningProgressData.behind.count +
                                  learningProgressData.on_track.count
                                : "--"}
                            </span>
                            <span>No. of students</span>
                          </div>

                          <div className="text-center bg-light rounded border py-2 flex-fill">
                            <button
                              className="btn btn-sm btn-primary p-0 px-2"
                              title="Refresh cache data"
                              onClick={clearCache}
                            >
                              Refresh
                            </button>
                            <div className="m-0 p-0 small">
                              Last Updated : {cacheLoading ? "Loading..." : cachedTime}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-fill d-flex overflow-hidden">
                    <div className="card shadow-sm border flex-grow-1 w-100">
                      <div className="card-body d-flex flex-column h-100">
                        <LiveSession
                          attendanceReport={attendanceReport}
                          selectedSession={selectedSession}
                          onSessionChange={setSelectedSession}
                          totalAttendance={totalAttendance}
                          attendanceGradient={attendanceGradient}
                          attendanceValues={attendanceValues}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-xxl-5 d-flex flex-column overflow-hidden">
                <div className="card shadow-sm border flex-grow-1 w-100 d-flex flex-column">
                  <div className="card-body d-flex flex-column flex-grow-1">
                    <div className="flex-grow-1 d-flex flex-column min-h-0">
                      <LearningProgress
                        learningProgressData={learningProgressData}
                        loading={false}
                        onShowMore={handleShowMore}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Candidates Performance | Category + Upcoming Events */}
            <div className="row g-3 mt-0 align-items-stretch">
              <div className="col-12 col-xl-7 d-flex flex-column overflow-hidden">
                <div className="flex-grow-1 d-flex flex-column min-h-0 w-100">
                  <CandidatesPerformance
                    performanceData={performanceData}
                    loading={false}
                    performanceFilter={performanceFilter}
                    width="100%"
                    isProjectSelected={!!selectedProject}
                  />
                </div>
              </div>

              <div className="col-12 col-xl-5 d-flex flex-column gap-3 overflow-hidden">
                <div className="flex-shrink-0">
                  <Category categoryStats={categoryStats} />
                </div>
                <div className="flex-grow-1 d-flex flex-column min-h-0">
                  <UpcomingEvents
                    upcomingEventsData={upcomingEventsData}
                    loading={false}
                  />
                </div>
              </div>
            </div>

            {selectedSubject && !selectedProject && (
              <div className="row g-3 mt-0">
                <div className="col-12">
                  <Subjects
                    subjectsData={subjectsData}
                    subjectsLoading={false}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
